// src/components/Dashboard.jsx
import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const StatCard = ({ title, value, sub, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    orange: "from-orange-500 to-orange-600",
    purple: "from-purple-500 to-purple-600",
  };

  return (
    <div className={`rounded-2xl shadow-sm p-5 bg-gradient-to-br ${colorClasses[color]} text-white`}>
      <p className="text-sm opacity-90">{title}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs opacity-80 mt-1">{sub}</p>}
    </div>
  );
};

const Table = ({ columns, data }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
    <table className="w-full text-sm">
      <thead className="bg-slate-50">
        <tr>
          {columns.map((c, i) => (
            <th
              key={i}
              className="text-left font-semibold px-4 py-3 text-slate-600"
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="px-4 py-3 text-center text-slate-400">
              Belum ada transaksi
            </td>
          </tr>
        ) : (
          data.map((row, i) => (
            <tr key={i} className="border-t hover:bg-slate-50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-slate-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const SectionHeader = ({ title, children }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-yellow-100 text-yellow-700",
    active: "bg-green-100 text-green-700",
    completed: "bg-blue-100 text-blue-700",
    rejected: "bg-red-100 text-red-700",
    draft: "bg-gray-100 text-gray-700",
  };
  return colors[status] || "bg-gray-100 text-gray-700";
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function Dashboard({ Icon, api, auth, onNavigate }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userRole = auth?.user?.role || "anggota";
  const isAdmin = userRole === "administrator" || userRole === "pegawai";

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/dashboard");
      setDashboardData(data);
    } catch (e) {
      console.error("Error fetching dashboard:", e);
      setError("Gagal memuat data dashboard. Pastikan backend berjalan & token valid.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Export PDF ───────────────────────────────────────────────────────────
  const handleUnduhLaporan = () => {
    if (!dashboardData?.recent_transactions?.length) return;

    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text("Laporan Transaksi Terbaru", 14, 15);
    doc.setFontSize(10);
    doc.text(
      `Dicetak: ${new Date().toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}`,
      14,
      22
    );

    const tableData = dashboardData.recent_transactions.map((t) => [
      formatDate(t.date),
      t.description,
      t.type,
      t.amount_formatted,
      t.status,
    ]);

    autoTable(doc, {
      head: [["Tanggal", "Deskripsi", "Tipe", "Jumlah", "Status"]],
      body: tableData,
      startY: 28,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [59, 130, 246] },
    });

    doc.save("laporan-transaksi.pdf");
  };
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Memuat dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-5">
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchDashboard}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="rounded-2xl bg-yellow-50 border border-yellow-200 p-5">
        <p className="text-yellow-700">Data dashboard tidak tersedia</p>
      </div>
    );
  }

  const { summary, recent_transactions, latest_loan_application, pending_loan_applications, member } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Header with Member Info (for anggota) */}
      {member && (
        <div className="rounded-2xl bg-white border p-5">
          <h2 className="text-xl font-bold text-slate-800">Selamat Datang, {member.name}!</h2>
          <p className="text-sm text-slate-600">No. Anggota: {member.member_no}</p>
        </div>
      )}

      <SectionHeader title="Ringkasan">
        <button
          onClick={handleUnduhLaporan}
          className="px-3 py-2 rounded-xl border text-sm hover:bg-slate-50 flex items-center gap-2"
        >
          <Icon name="download" />
          Unduh Laporan
        </button>
      </SectionHeader>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={summary.saldo_simpanan.label}
          value={summary.saldo_simpanan.formatted}
          sub={summary.saldo_simpanan.description}
          color="blue"
        />
        <StatCard
          title={summary.sisa_pinjaman.label}
          value={summary.sisa_pinjaman.formatted}
          sub={summary.sisa_pinjaman.description}
          color="orange"
        />
        <StatCard
          title={summary.angsuran_bulan_ini.label}
          value={summary.angsuran_bulan_ini.formatted}
          sub={summary.angsuran_bulan_ini.description}
          color="green"
        />
        <StatCard
          title={summary.pengajuan_menunggu.label}
          value={summary.pengajuan_menunggu.count.toString()}
          sub={summary.pengajuan_menunggu.description}
          color="purple"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent Transactions */}
        <div className="space-y-3">
          <SectionHeader title="Riwayat Transaksi Terbaru" />
          <Table
            columns={["Tanggal", "Deskripsi", "Jumlah", "Status"]}
            data={recent_transactions.map((t) => [
              formatDate(t.date),
              <div key="desc">
                <div className="font-medium">{t.description}</div>
                <div className="text-xs text-slate-500">{t.type}</div>
              </div>,
              <span
                key="amount"
                className={`font-semibold ${
                  t.amount > 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {t.amount_formatted}
              </span>,
              <span key="status" className="text-xs px-2 py-1 rounded-lg bg-green-100 text-green-700">
                {t.status}
              </span>,
            ])}
          />
        </div>

        {/* Latest Loan Application (for anggota) or Pending Applications (for admin) */}
        <div className="space-y-3">
          {isAdmin && pending_loan_applications ? (
            <>
              <SectionHeader title="Pengajuan Pinjaman Menunggu" />
              <div className="rounded-2xl bg-white border overflow-hidden">
                {pending_loan_applications.length === 0 ? (
                  <div className="p-5 text-center text-slate-400">
                    Tidak ada pengajuan menunggu
                  </div>
                ) : (
                  <div className="divide-y">
                    {pending_loan_applications.map((loan) => (
                      <div key={loan.id} className="p-4 hover:bg-slate-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-slate-800">{loan.member_name}</p>
                            <p className="text-xs text-slate-500">{loan.member_no}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-lg text-xs ${getStatusColor(loan.status)}`}>
                            {loan.status_label}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Jenis</p>
                            <p className="font-medium">{loan.jenis_pinjaman}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Jumlah</p>
                            <p className="font-medium text-blue-600">{loan.jumlah_formatted}</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          Diajukan: {formatDate(loan.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : latest_loan_application ? (
            <>
              <SectionHeader title="Pengajuan Pinjaman Terakhir" />
              <div className="rounded-2xl bg-white border p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Jenis Pinjaman</p>
                    <p className="font-medium">{latest_loan_application.jenis_pinjaman}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Jumlah</p>
                    <p className="font-medium text-blue-600">{latest_loan_application.jumlah_formatted}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Tenor</p>
                    <p className="font-medium">{latest_loan_application.tenor}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <span className={`px-2 py-1 rounded-lg text-xs ${getStatusColor(latest_loan_application.status)}`}>
                      {latest_loan_application.status_label}
                    </span>
                  </div>
                </div>
                {latest_loan_application.contract_no && (
                  <div>
                    <p className="text-xs text-slate-500">No. Kontrak</p>
                    <p className="font-medium">{latest_loan_application.contract_no}</p>
                  </div>
                )}
                {/* ✅ Kirim loanId sekalian saat navigasi */}
                <button
                  onClick={() => onNavigate("Pinjaman", latest_loan_application.id)}
                  className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 font-medium"
                >
                  Lihat Detail
                </button>
              </div>
            </>
          ) : (
            <>
              <SectionHeader title="Pengajuan Pinjaman Terakhir" />
              <div className="rounded-2xl bg-white border p-5 text-center text-slate-400">
                Belum ada pengajuan pinjaman
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}